/** tm/legal/LegalBatchSteps.tsx — 상태 줄 ⓘ 풍선: 새벽 일괄 수집 단계별 결과 · 다음 수집 시각 (단계 정보가 없으면 법령·뉴스 마지막 수집만) */
import { C, T } from '../components/ui';
import { whenLabel, shortTime, num, AMBER, MUTED } from './legalTheme';
import type { BatchStep, LegalSummary } from './legalTypes';

const STEP_LABEL: Record<string, string> = {
  legal: '법령해석·판례', laws: '법령 개정', details: '상세·요지', bills: '국회 법안', notices: '입법예고', cabinet: '국무회의 의결',
  news: '뉴스', relink: '주제 재연결·검증', sms: '아침 문자',
};
const ICON: Record<string, { t: string; c: string }> = {
  ok: { t: '완료', c: C.green }, partial: { t: '일부 실패', c: AMBER }, error: { t: '실패', c: C.red },
  running: { t: '진행 중', c: C.accent }, pending: { t: '대기', c: MUTED }, skipped: { t: '건너뜀', c: MUTED },
};

function fallbackSteps(s: LegalSummary | null): BatchStep[] {
  const out: BatchStep[] = [];
  if (s?.lastRun) out.push({ key: 'legal', label: '법령·판례·해석', status: s.lastRun.status, new_count: s.lastRun.new_count, error: s.lastRun.error, finished_at: s.lastRun.finished_at });
  if (s?.news?.lastRun) out.push({ key: 'news', status: s.news.lastRun.status, finished_at: s.news.lastRun.finished_at });
  return out;
}

/** 다음 새벽 4시 (KST) — 서버 nextRun 이 없을 때 */
function nextFour(): string {
  const kst = new Date(Date.now() + 9 * 3600e3);
  const d = new Date(Date.UTC(kst.getUTCFullYear(), kst.getUTCMonth(), kst.getUTCDate(), 4 - 9));
  if (d.getTime() <= Date.now()) d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString();
}

export default function LegalBatchSteps({ summary }: { summary: LegalSummary | null }) {
  const run = summary?.batch?.lastRun || null;
  const steps = run?.steps?.length ? run.steps : fallbackSteps(summary);
  const next = summary?.nextRun || summary?.batch?.nextRun || nextFour();
  return (
    <div role="tooltip" style={{
      position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 70, width: 'min(320px, calc(100vw - 24px))', background: C.white, border: `1px solid ${C.border}`,
      borderRadius: 10, boxShadow: '0 10px 28px rgba(17,24,39,.16)', padding: '10px 12px', fontSize: T.sm, color: C.text, lineHeight: 1.5,
    }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>새벽 일괄 수집 {run?.started_at ? <span style={{ fontWeight: 400, color: MUTED, fontSize: T.sm }}>{shortTime(run.started_at)} 시작{run.trigger === 'manual' ? ' (지금 수집)' : ''}</span> : null}</div>
      {steps.length === 0 && <div style={{ color: MUTED, fontSize: T.sm }}>아직 수집 기록이 없습니다.</div>}
      {steps.map(s => {
        const ic = ICON[s.status] || { t: s.status, c: MUTED };
        return (
          <div key={s.key} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '3px 0', borderTop: `1px solid ${C.bg}` }}>
            <span style={{ flex: 1 }}>{s.label || STEP_LABEL[s.key] || s.key}</span>
            {s.new_count != null ? <span style={{ color: C.sub, fontVariantNumeric: 'tabular-nums', fontSize: T.sm }}>새 글 {num(s.new_count)}</span>
              : s.note ? <span style={{ color: C.sub, fontSize: T.sm, textAlign: 'right', maxWidth: 150 }}>{s.note}</span> : null}
            <b style={{ color: ic.c, fontSize: T.sm, minWidth: 44, textAlign: 'right' }}>{ic.t}</b>
          </div>
        );
      })}
      {steps.some(s => s.error) && <div style={{ marginTop: 4, fontSize: T.sm, color: C.red, wordBreak: 'break-all' }}>{steps.find(s => s.error)?.error}</div>}
      {summary?.backfill?.active && (
        <div style={{ marginTop: 8, fontSize: T.sm, color: C.sub, lineHeight: 1.6 }}>
          과거 자료 채우는 중{summary.backfill.total ? ` ${num(summary.backfill.done ?? 0)}/${num(summary.backfill.total)}` : ''} (매일 밤) — 채운 글은 새 소식으로 알리지 않습니다
        </div>
      )}
      {summary?.firstRun && <div style={{ marginTop: 4, fontSize: T.sm, color: C.sub }}>첫 수집이라 지금까지 쌓인 글은 기준선으로 모았습니다</div>}
      <div style={{ marginTop: 8, fontSize: T.sm, color: MUTED }}>
        다음 자동 수집 {whenLabel(next).startsWith('오늘') ? whenLabel(next) : `내일 ${shortTime(next).slice(6)}`} · 뉴스도 이때 함께 모읍니다
      </div>
    </div>
  );
}
